import { AttributeClient, AttributeValueClient } from '@frontend/attribute/api';
import { Attribute, CreateAttribute } from '@frontend/attribute/types';
import { EntityType } from '@frontend/common';
import { updateField, useAccountRepository } from '@frontend/repository';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';

const NAME_TRANSLATION_FIELDS = ['name_nl_be', 'name_en_us', 'name_fr_fr', 'name_de_de'] as const;
export const FORM_ID = 'create-attribute-form';

export const useCreateAttributeModal = () => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const [values, setValues] = useState<string[]>(['']);

    const nameTranslationsModified = useRef<boolean>(false);
    const previousName = useRef<string>('');

    const form = useAppSelector((state) => state.forms);
    const formValues = useMemo(() => {
        const f = form[FORM_ID];
        if (!f) return {};
        return f.value;
    }, [form]);

    useEffect(() => {
        const currentName = (formValues.name as string) ?? '';

        // Check if any name translation was manually modified
        if (!nameTranslationsModified.current) {
            const anyNameTranslationModified = NAME_TRANSLATION_FIELDS.some((field) => {
                const fieldValue = (formValues[field] as string) ?? '';
                const expectedValue = previousName.current;
                return fieldValue !== '' && fieldValue !== expectedValue && fieldValue !== currentName;
            });
            if (anyNameTranslationModified) {
                nameTranslationsModified.current = true;
            }
        }

        // Auto-fill name translations if they haven't been manually modified
        if (currentName !== previousName.current) {
            if (!nameTranslationsModified.current) {
                NAME_TRANSLATION_FIELDS.forEach((field) => {
                    dispatch(updateField({ formId: FORM_ID, key: field, value: currentName }));
                });
            }
            previousName.current = currentName;
        }
    }, [formValues, dispatch]);

    const addValue = () => {
        setValues([...values, '']);
    };

    const removeValue = (index: number) => {
        setValues((prev) => prev.filter((_, i) => i !== index));
    };

    const updateValue = (value: string, index: number) => {
        setValues((prev) => prev.map((p, i) => (i === index ? value : p)));
    };

    const onDragCallback = (items: { id: string; value: string | undefined | null }[]) => {
        setValues(items.map((i) => i.value || ''));
    };

    const onSubmit = async (v: CreateAttribute): Promise<Attribute> => {
        const attribute = await AttributeClient.postAttribute(currentAccount, v);
        const filteredValues = values.filter((value) => value && value.trim() !== '');
        const valueRequests = filteredValues.map((value, index) =>
            AttributeValueClient.postAttributeValue(attribute.account_id, attribute.id, { value: value, order: index })
        );
        await Promise.all(valueRequests);
        return attribute;
    };

    const submitDisabled = (v: CreateAttribute) => {
        return !v.name;
    };

    return {
        values,
        addValue,
        removeValue,
        updateValue,
        onDragCallback,
        onSubmit,
        submitDisabled,
        type: EntityType.ATTRIBUTE,
        formId: FORM_ID
    };
};
